import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import { rankItems } from "../fuzzy";
import { eventInside, onEscapeKey } from "../outsideDismiss";

export type SearchableOption = {
  value: string;
  label: string;
  /** Second line under the label (store, location, camera id, …). */
  hint?: string;
  /** Extra text that should match a query without being displayed. */
  keywords?: string;
  /** Small right-aligned tag, e.g. "Simulation" or "Non-AI". */
  badge?: string;
  badgeClass?: string;
  disabled?: boolean;
  /** Renders a divider + caption above this option. */
  groupLabel?: string;
  /** Gradient start color for the group rule (`from-bf-violet`, …). */
  groupAccent?: string;
};

/**
 * The dropdown used wherever a store, camera or profile is picked.
 *
 * Two things it does that a plain listbox does not:
 *
 *  - Ranks by best match rather than requiring a substring (see `fuzzy.ts`),
 *    because nobody remembers whether the store is "Down-Town #4" or
 *    "downtown 4".
 *  - Renders the list in a PORTAL, positioned in viewport coordinates. An
 *    absolutely-positioned list is clipped by any ancestor that hides its
 *    overflow — which is how the store picker ended up invisible inside the
 *    app header — and `.bf-card`'s backdrop-filter makes even `position: fixed`
 *    resolve against the card instead of the viewport. Portalling is the only
 *    fix that holds wherever the control is dropped.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  disabled,
  className = "",
  id,
  ariaLabel,
  title,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches.",
  searchThreshold = 7,
  footer,
  compact = false,
  icon,
  variant = "default",
  triggerLabel,
  triggerHint,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  disabled?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
  title?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  searchThreshold?: number;
  /** Rendered at the bottom of the open list. `close` dismisses the list. */
  footer?: (close: () => void) => ReactNode;
  /** Collapsed sidebar: icon above the name and chevron. */
  compact?: boolean;
  icon?: ReactNode;
  /** Sidebar trigger: menu always opens underneath the control. */
  variant?: "default" | "nav";
  /** Visible text on a `nav` trigger. Falls back to the selected option. */
  triggerLabel?: string;
  /** Second line under a `nav` trigger (current store / profile name). */
  triggerHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<DOMRect | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setRect(r);
    };
    place();
    // `true` captures scrolls of any ancestor, not just the window: these
    // controls live inside scrollable page bodies and dialogs.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    let downOutside = false;
    const onDown = (e: PointerEvent) => {
      downOutside = !eventInside(e, wrapRef.current, listRef.current);
    };
    const onUp = (e: PointerEvent) => {
      if (downOutside && !eventInside(e, wrapRef.current, listRef.current)) {
        setOpen(false);
      }
    };
    const onKey = onEscapeKey(() => setOpen(false));
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("keydown", onKey, true);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const showSearch = options.length >= searchThreshold;

  const visible = useMemo(() => {
    if (!query.trim()) return options;
    return rankItems(options, query, (o) => [
      o.label,
      o.value,
      o.hint,
      o.keywords,
    ]).map((r) => r.item);
  }, [options, query]);

  const close = () => setOpen(false);
  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  // Flip above the trigger when there is more room there, so a control near
  // the bottom of the page still shows its list. Nav (sidebar) always drops
  // under the row — including the collapsed rail — so the chevron means
  // "menu below", not a flyout to the right of a dead icon.
  const below = rect ? window.innerHeight - rect.bottom : 0;
  const above = rect ? rect.top : 0;
  const dropUnder = variant === "nav" || !compact;
  const dropUp = dropUnder && rect != null && below < 280 && above > below;
  const maxHeight = Math.max(
    180,
    Math.min(
      340,
      (dropUp ? above : dropUnder || !rect ? below : window.innerHeight - rect.top) - 16,
    ),
  );
  const listWidth = rect ? Math.max(rect.width, 240) : 240;
  const listLeft = rect
    ? Math.min(
        dropUnder ? rect.left : rect.right + 4,
        Math.max(8, window.innerWidth - 8 - listWidth),
      )
    : 0;

  let lastGroup: string | undefined;

  return (
    <div ref={wrapRef} className={"relative " + className}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title ?? selected?.label}
        onClick={() => setOpen((v) => !v)}
        className={
          variant === "nav"
            ? "flex w-full rounded-lg text-sm font-medium text-bf-muted transition hover:bg-white/5 hover:text-bf-text disabled:cursor-not-allowed disabled:opacity-50 " +
              (compact
                ? "flex-col items-center justify-center gap-0.5 px-1 py-1.5"
                : "items-start gap-3 px-3 py-1.5")
            : compact
              ? "flex w-full flex-col items-center justify-center gap-0.5 rounded-lg py-1.5 text-bf-muted transition hover:bg-white/5 hover:text-bf-text disabled:cursor-not-allowed disabled:opacity-50"
              : "flex w-full items-center justify-between gap-2 rounded-lg border border-white/10 bg-bf-surface/60 py-2 pl-3 pr-2.5 text-sm font-medium text-bf-text transition hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-50"
        }
      >
        {variant === "nav" ? (
          <>
            {icon && (
              <span
                className={
                  "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center" +
                  (compact ? "" : " mt-px")
                }
              >
                {icon}
              </span>
            )}
            {compact ? (
              <span className="max-w-full truncate px-0.5 text-center text-[11px] font-medium leading-tight text-bf-muted">
                {triggerHint ?? triggerLabel ?? selected?.label ?? placeholder}
              </span>
            ) : (
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[13px] font-medium leading-5">
                  {triggerLabel ?? selected?.label ?? placeholder}
                </span>
                {triggerHint && (
                  <span className="block truncate text-[11px] font-normal leading-4 text-bf-muted">
                    {triggerHint}
                  </span>
                )}
              </span>
            )}
            <ChevronDown
              size={compact ? 12 : 14}
              className={
                "shrink-0 text-bf-muted/70 transition " +
                (open ? "rotate-180" : "") +
                (compact ? "" : " mt-0.5")
              }
            />
          </>
        ) : compact ? (
          <>
            {icon}
            <ChevronDown
              size={14}
              className={"shrink-0 text-bf-muted transition " + (open ? "rotate-180" : "")}
            />
          </>
        ) : (
          <>
            <span className="truncate">{selected?.label ?? placeholder}</span>
            <ChevronDown
              size={16}
              className={"shrink-0 text-bf-muted transition " + (open ? "rotate-180" : "")}
            />
          </>
        )}
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={listRef}
            className={
              variant === "nav"
                ? "fixed z-[110] overflow-hidden rounded-xl border border-white/30 bg-ink shadow-[0_18px_50px_-12px_rgba(0,0,0,0.8)] ring-1 ring-bf-violet/45"
                : "fixed z-[110] overflow-hidden rounded-lg border border-white/10 bg-bf-surface shadow-xl shadow-black/50"
            }
            style={{
              left: listLeft,
              width: listWidth,
              ...(dropUp
                ? { bottom: window.innerHeight - rect.top + (variant === "nav" ? 8 : 4) }
                : { top: (dropUnder ? rect.bottom + (variant === "nav" ? 8 : 4) : rect.top) }),
            }}
          >
            {showSearch && (
              <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
                <Search size={14} className="shrink-0 text-bf-muted" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpen(false);
                      return;
                    }
                    if (e.key === "Enter" && visible.length > 0) {
                      e.preventDefault();
                      const first = visible.find((o) => !o.disabled);
                      if (first) pick(first.value);
                    }
                  }}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-[13px] text-bf-text outline-none placeholder:text-bf-muted"
                  aria-label={searchPlaceholder}
                />
              </div>
            )}
            <div role="listbox" className="overflow-auto p-1.5" style={{ maxHeight }}>
              {visible.length === 0 && (
                <p className="px-2.5 py-3 text-[13px] text-bf-muted">{emptyMessage}</p>
              )}
              {visible.map((o) => {
                const active = o.value === value;
                // Group captions only make sense in the unfiltered list; once
                // results are ranked by score they no longer arrive grouped.
                const heading =
                  !query.trim() && o.groupLabel && o.groupLabel !== lastGroup
                    ? o.groupLabel
                    : undefined;
                if (!query.trim() && o.groupLabel) lastGroup = o.groupLabel;
                return (
                  <div key={o.value}>
                    {heading && (
                      <div className="mx-1.5 mb-1 mt-2.5 flex items-center gap-2 first:mt-1">
                        <p className="shrink-0 text-[11px] font-medium tracking-[0.06em] text-white/65">
                          {heading}
                        </p>
                        <span
                          className={
                            "h-px min-w-6 flex-1 bg-gradient-to-r to-transparent " +
                            (o.groupAccent ?? "from-bf-violet")
                          }
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      disabled={o.disabled}
                      onClick={() => pick(o.value)}
                      className={
                        "flex w-full items-start justify-between gap-2 rounded-md px-2.5 py-2 text-left transition disabled:opacity-40 " +
                        (active
                          ? "bg-bf-violet/25 text-white"
                          : "text-bf-text hover:bg-white/5")
                      }
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium leading-5">
                          {o.label}
                        </span>
                        {o.hint && (
                          <span className="mt-0.5 block truncate text-[11px] font-normal leading-4 text-bf-muted">
                            {o.hint}
                          </span>
                        )}
                      </span>
                      <span className="flex shrink-0 items-center gap-2 pt-0.5">
                        {o.badge && (
                          <span
                            className={
                              "rounded-full px-1.5 py-0.5 text-xs font-medium " +
                              (o.badgeClass || "bg-white/10 text-bf-muted")
                            }
                          >
                            {o.badge}
                          </span>
                        )}
                        {active && <Check size={14} className="text-bf-violet" />}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
            {footer && (
              <div className="border-t border-white/10 p-1">{footer(close)}</div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
